import { useApiGet } from '@/api/apiRequests.ts';
import { downloadReport, getOrps } from '@/api/services';
import { getFilters } from '@/api/services/getFilters.ts';
import { PageLoader } from '@/components/PageLoader.tsx';
import { REPORT_IN_BACKGROUND_THRESHOLD } from '@/constants';
import { AxiosError } from 'axios';
import { Button } from 'primereact/button';
import { Checkbox } from 'primereact/checkbox';
import { InputText } from 'primereact/inputtext';
import { MultiSelect, MultiSelectChangeEvent } from 'primereact/multiselect';
import { FC, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const Reports: FC = () => {
  const [orpsData, setOrpsData] = useState();
  const [categoryData, setCategoryData] = useState();
  const [selectedOrps, setSelectedOrps] = useState<string[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [introduction, setIntroduction] = useState<boolean>(false);
  const [email, setEmail] = useState<string>('');
  const [emailTouched, setEmailTouched] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const { data } = useApiGet<any, AxiosError>(['orps'], async () => await getOrps(), {
    enabled: true,
  });
  const { data: filterData } = useApiGet<any, AxiosError>(['filters'], async () => await getFilters(), {
    enabled: true,
  });

  useEffect(() => {
    const transformedOrps = data?.map((orp: any) => ({
      ...orp,
      label: orp.municipality?.name || orp.municipality?.orp_name || '',
    })) || [];
    setOrpsData(transformedOrps);
    setCategoryData(filterData?.category);
  }, [data, filterData]);

  const isAsyncMode = useMemo(
    () => Array.isArray(selectedOrps) && selectedOrps.length > REPORT_IN_BACKGROUND_THRESHOLD,
    [selectedOrps]
  );

  const isEmailValid = EMAIL_REGEX.test(email.trim());

  const isFormValid = useMemo(() => {
    if (!Array.isArray(selectedOrps) || selectedOrps.length === 0) return false;
    if (isAsyncMode && !isEmailValid) return false;
    return true;
  }, [selectedOrps, isAsyncMode, isEmailValid]);

  const handleDownloadReport = async () => {
    let orpsArray: string[];
    let catArray: string[];
    setIsLoading(true);

    if (Array.isArray(selectedOrps) && selectedOrps.length > 0) {
      orpsArray = selectedOrps.map(
        (item: any) => item.label || item.municipality?.name || item.municipality?.orp_name || ''
      );
    } else {
      orpsArray = [];
    }

    if (Array.isArray(selectedCategories) && selectedCategories.length > 0) {
      catArray = selectedCategories.map((item: any) => item);
    } else {
      catArray = [];
    }

    try {
      const result = await downloadReport(orpsArray, catArray, introduction, isAsyncMode ? email.trim() : undefined);
      if (result.kind === 'queued') {
        toast.success(
          result.message ||
            `Generování spuštěno na pozadí. Odkaz bude odeslán na ${email.trim()} (platnost 3 dny).`
        );
      }
    } catch {
      // toast already shown inside downloadReport
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <h1 className={'text-2xl font-bold mb-8 text-white'}>Reports</h1>
      <div className={'flex flex-col justify-center items-center gap-4'}>
        <MultiSelect
          value={selectedOrps}
          onChange={(e: MultiSelectChangeEvent) => setSelectedOrps(e.value)}
          options={orpsData}
          optionLabel="label"
          filter={true}
          placeholder="Choose cities"
          maxSelectedLabels={7}
          className="w-full md:max-w-2/3"
        />
        <MultiSelect
          value={selectedCategories}
          onChange={(e: MultiSelectChangeEvent) => setSelectedCategories(e.value)}
          options={categoryData ? Object.entries(categoryData).map(([value, label]) => ({ label, value })) : []}
          optionLabel="label"
          filter={true}
          placeholder="Choose category"
          maxSelectedLabels={7}
          className="w-full md:max-w-2/3"
        />
        <div className="flex items-center">
          <Checkbox
            inputId={'introduction'}
            name={'introduction'}
            checked={introduction}
            onChange={(e) => setIntroduction(e.checked ?? false)}
          />
          <label htmlFor={'introduction'} className="ml-2">
            Include introduction
          </label>
        </div>

        {isAsyncMode && (
          <div className="w-full md:max-w-2/3 flex flex-col gap-3">
            <div
              className="flex items-start gap-3 rounded-lg border border-[#14A4BCFF]/30 border-l-4 border-l-[#14A4BCFF] bg-[#E8F7FA] px-4 py-3 shadow-sm"
              role="status"
            >
              <svg
                className="mt-0.5 h-5 w-5 shrink-0 text-[#14A4BCFF]"
                viewBox="0 0 20 20"
                fill="currentColor"
                aria-hidden="true"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 1 0 0-16 8 8 0 0 0 0 16Zm.75-11.25a.75.75 0 0 0-1.5 0v3.5a.75.75 0 0 0 1.5 0v-3.5ZM10 15a1 1 0 1 0 0-2 1 1 0 0 0 0 2Z"
                  clipRule="evenodd"
                />
              </svg>
              <p className="text-sm leading-relaxed text-[#1B1531]">
                Vybrali jste více než {REPORT_IN_BACKGROUND_THRESHOLD} obcí — report bude vygenerován na
                pozadí a odkaz dorazí e-mailem. Během generování můžete zavřít prohlížeč.
              </p>
            </div>
            <label htmlFor="report-email" className="font-bold text-[#1B1531]">
              E-mail pro doručení odkazu
            </label>
            <InputText
              id="report-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onBlur={() => setEmailTouched(true)}
              placeholder="vase.adresa@example.com"
              invalid={emailTouched && !isEmailValid}
              aria-invalid={emailTouched && !isEmailValid}
              aria-describedby="report-email-error"
            />
            {emailTouched && !isEmailValid && (
              <small id="report-email-error" className="text-sm text-red-600">
                Zadejte e-mailovou adresu ve správném tvaru (např. jmeno@domena.cz).
              </small>
            )}
          </div>
        )}

        <Button
          className={'btn-primary'}
          style={{ borderRadius: '6px' }}
          onClick={handleDownloadReport}
          disabled={!isFormValid || isLoading}
          loading={isLoading}
        >
          {isAsyncMode ? 'Generate & email' : 'Generate report'}
        </Button>
      </div>
      <PageLoader visible={isLoading && !isAsyncMode} />
    </>
  );
};
